import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type Density = "comfortable" | "compact" | "dense";

interface DensityApi {
  density: Density;
  setDensity: (next: Density) => void;
}

const DensityContext = createContext<DensityApi | null>(null);

/**
 * Density only.
 *
 * (H) doc 40 §15: **the dark theme is deferred.** The previous version of this
 * file carried a light/dark/system switch and a `data-theme` attribute, and
 * doc 40 §16 records why that was wrong — effort spent on a theme nobody had
 * asked for while the light theme itself was wrong. It is deleted rather than
 * left dormant, because a half-wired theme switch is worse than none: it
 * invites components to hardcode a `dark:` variant against tokens that do not
 * exist yet.
 *
 * The token architecture still supports a second theme — every role in
 * `packages/ui/src/tokens/source.ts` is indirected through a primitive — so
 * adding one later is a role map and a `[data-theme="dark"]` block in the
 * generated CSS, not a refactor of this file's callers.
 *
 * Density, by contrast, ships now: doc 40 §10 makes it a persisted per-user
 * preference, and it is stamped on `<html data-density>` so the CSS variables
 * in `tokens.css` switch row heights with one attribute rather than a
 * per-component conditional.
 */
export function ThemeProvider({
  children,
  initialDensity = "compact",
}: {
  children: ReactNode;
  initialDensity?: Density;
}) {
  const [density, setDensityState] = useState<Density>(initialDensity);

  useEffect(() => {
    document.documentElement.setAttribute("data-density", density);
    // Light only, stated explicitly: the browser must not paint a dark form
    // control on a light page because the OS is in dark mode.
    document.documentElement.style.colorScheme = "light";
  }, [density]);

  const setDensity = useCallback((next: Density) => setDensityState(next), []);
  const api = useMemo<DensityApi>(
    () => ({ density, setDensity }),
    [density, setDensity],
  );

  return (
    <DensityContext.Provider value={api}>{children}</DensityContext.Provider>
  );
}

export function useDensity(): DensityApi {
  const ctx = useContext(DensityContext);
  if (ctx === null)
    throw new Error("useDensity must be used inside <ThemeProvider>.");
  return ctx;
}
